import { useEffect, useState } from 'react'
import { fetchAnnualRevenue, type AnnualRevenue } from '../api/annualRevenue'
import { Button, Card, CardContent, CardHeader, DataTable, Select } from './ui'

const eur = (v: number): string =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v)
const monthName = (m: number): string =>
  new Intl.DateTimeFormat('it-IT', { month: 'long' }).format(new Date(2024, m - 1, 1))

/**
 * R5-12 - "Fatturato per anno": per ogni anno dal 2023, pratiche firmate e pagate, valore pagato, base provvigionale,
 * storni, ricorrenti e netto, con dettaglio mensile a scomparsa. La sezione sparisce se l'utente non vede gli importi (403).
 */
export function AnnualRevenueSection() {
  const [data, setData] = useState<AnnualRevenue | null>(null)
  const [hidden, setHidden] = useState(false)
  const [error, setError] = useState(false)
  const [year, setYear] = useState<number | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let alive = true
    fetchAnnualRevenue()
      .then(d => { if (alive) { setData(d); setYear(d.currentYear) } })
      .catch((e: unknown) => {
        if (!alive) return
        if ((e as { status?: number })?.status === 403) setHidden(true)
        else setError(true)
      })
    return () => { alive = false }
  }, [])

  if (hidden) return null
  const selected = data?.years.find(y => y.year === year)

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        title="Fatturato per anno"
        subtitle="Incassato = pagamenti registrati (es. Numbers) per mese di competenza; incassato netto = incassato + storni + ricorrenti. Lordo e pagato vengono dagli importi scritti sulle pratiche (le pratiche Numbers non hanno importo)"
        actions={data && (
          <Select label="Anno" value={year ?? ''} onChange={e => setYear(Number(e.target.value))}>
            {data.years.map(y => <option key={y.year} value={y.year}>{y.year}</option>)}
          </Select>
        )}
      />
      <CardContent>
        {error && <p className="text-muted">Impossibile caricare il fatturato.</p>}
        {!data && !error && <p className="text-muted">Caricamento...</p>}
        {data && (
          <>
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Anno</th>
                  <th className="text-end">Firmati</th>
                  <th className="text-end">Pagati</th>
                  <th className="text-end">Valore lordo</th>
                  <th className="text-end">Valore pagato</th>
                  <th className="text-end">Base provvigionale</th>
                  <th className="text-end">Incassato</th>
                  <th className="text-end">Storni</th>
                  <th className="text-end">Ricorrenti</th>
                  <th className="text-end">Incassato netto</th>
                </tr>
              </thead>
              <tbody>
                {data.years.map(y => (
                  <tr key={y.year} className={y.year === year ? 'fw-bold' : undefined}>
                    <td>{y.year}</td>
                    <td className="text-end md-num">{y.signed}</td>
                    <td className="text-end md-num">{y.paid}</td>
                    <td className="text-end md-num">{eur(y.grossValue)}</td>
                    <td className="text-end md-num">{eur(y.paidValue)}</td>
                    <td className="text-end md-num">{eur(y.commissionBasis)}</td>
                    <td className="text-end md-num">{eur(y.payments)}</td>
                    <td className="text-end md-num">{eur(y.storni)}</td>
                    <td className="text-end md-num">{eur(y.recurring)}</td>
                    <td className="text-end md-num">{eur(y.net)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            {selected && (
              <div className="mt-2">
                <Button variant="text" onClick={() => setOpen(o => !o)} aria-expanded={open}>
                  {open ? `Nascondi dettaglio mensile ${selected.year}` : `Dettaglio mensile ${selected.year}`}
                </Button>
                {open && (
                  <DataTable variant="plain">
                    <thead>
                      <tr>
                        <th>Mese</th>
                        <th className="text-end">Firmati</th>
                        <th className="text-end">Pagati</th>
                        <th className="text-end">Valore lordo</th>
                        <th className="text-end">Valore pagato</th>
                        <th className="text-end">Base provvigionale</th>
                        <th className="text-end">Incassato</th>
                        <th className="text-end">Storni</th>
                        <th className="text-end">Ricorrenti</th>
                        <th className="text-end">Incassato netto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.months.map(m => (
                        <tr key={m.month}>
                          <td className="text-capitalize">{monthName(m.month)}</td>
                          <td className="text-end md-num">{m.signed}</td>
                          <td className="text-end md-num">{m.paid}</td>
                          <td className="text-end md-num">{eur(m.grossValue)}</td>
                          <td className="text-end md-num">{eur(m.paidValue)}</td>
                          <td className="text-end md-num">{eur(m.commissionBasis)}</td>
                          <td className="text-end md-num">{eur(m.payments)}</td>
                          <td className="text-end md-num">{eur(m.storni)}</td>
                          <td className="text-end md-num">{eur(m.recurring)}</td>
                          <td className="text-end md-num">{eur(m.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
